const isLoadedInspection = (routeInspectionId?: string, loadedInspectionId?: string): boolean =>
  Boolean(routeInspectionId) && Boolean(loadedInspectionId) && routeInspectionId === loadedInspectionId;

/**
 * Prep is locked while an inspection is shared, so results stay consistent.
 * Inspections imported from VerifyAPI (`settings.isImported`) stay shared with
 * the org on purpose, and Prep is available on those.
 *
 * The exemption applies only once the loaded inspection is the one in the
 * route. While a linked inspection is still loading, the store still holds the
 * previous inspection, and that previous `isImported` must not unlock Prep.
 */
export const isPrepLocked = ({
  routeInspectionId,
  loadedInspectionId,
  isShared,
  isImported,
}: {
  routeInspectionId?: string;
  loadedInspectionId?: string;
  isShared: boolean;
  isImported?: boolean;
}): boolean => {
  if (!isShared) {
    return false;
  }

  if (!isLoadedInspection(routeInspectionId, loadedInspectionId)) {
    return true;
  }

  return isImported !== true;
};

/**
 * Decides whether a shared inspection should be bounced out of the Prep tab to
 * the Results tab.
 *
 * The redirect must only fire for the inspection that is actually loaded into
 * the store. When switching between linked inspections the route id changes
 * before the newly selected inspection finishes loading, so the store still
 * holds the previous inspection's shared state. Acting on that stale state
 * wrongly bounces a not-yet-run linked inspection to Results and shows the
 * previous inspection's data. Requiring the routed id to match the loaded id
 * guards against that race.
 *
 * VerifyAPI imports (`isImported`) are shared with the org and are not bounced.
 */
export const shouldRedirectSharedInspectionToResults = ({
  routeInspectionId,
  loadedInspectionId,
  isShared,
  isOnResultPage,
  isImported,
}: {
  routeInspectionId?: string;
  loadedInspectionId?: string;
  isShared: boolean;
  isOnResultPage: boolean;
  isImported?: boolean;
}): boolean => {
  if (!isLoadedInspection(routeInspectionId, loadedInspectionId)) {
    return false;
  }

  if (isOnResultPage) {
    return false;
  }

  return isPrepLocked({ routeInspectionId, loadedInspectionId, isShared, isImported });
};
